/**
 * Run Failure Intelligence — aggregated QA view for a single automation run.
 * Deterministic classification + existing evidence/healing/flake surfaces.
 * Recommendations are human-actionable; never silent publish/apply.
 */
import { z } from 'zod';
import { EXECUTION_FAILURE_CLASSES } from './execution-intelligence';
import { QualityFailureSchema } from './failures';

export const RUN_FAILURE_REMEDIATION_KINDS = [
  'open_flow_editor',
  'repick_locator',
  'review_pin_heal',
  'review_assertion',
  'check_environment',
  'check_test_data',
  'check_auth',
  'check_network',
  'open_failure_intelligence',
  'rerun_flow',
] as const;
export type RunFailureRemediationKind = (typeof RUN_FAILURE_REMEDIATION_KINDS)[number];

export const RUN_FAILURE_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type RunFailureConfidence = (typeof RUN_FAILURE_CONFIDENCE)[number];

export const RunFailureEvidenceSchema = z.object({
  hasTrace: z.boolean(),
  hasScreenshots: z.boolean(),
  hasHealingNarratives: z.boolean(),
  failedStepIndex: z.number().int().nullable(),
  failedStepTitle: z.string().nullable(),
  rawErrorPreview: z.string().nullable(),
});
export type RunFailureEvidence = z.infer<typeof RunFailureEvidenceSchema>;

export const RunFailureRemediationSchema = z.object({
  kind: z.enum(RUN_FAILURE_REMEDIATION_KINDS),
  title: z.string(),
  detail: z.string(),
  /** Human must approve/apply — never auto-applied by this endpoint. */
  requiresHumanApproval: z.literal(true),
  /** SPA hash or relative path; null when not navigable from this surface. */
  actionUrl: z.string().nullable(),
});
export type RunFailureRemediation = z.infer<typeof RunFailureRemediationSchema>;

export const HEALING_RECOMMENDATION_KINDS = [
  'propose_draft',
  'ambiguous',
  'no_healing',
  'insufficient',
  'review_required',
] as const;
export type HealingRecommendationKind = (typeof HEALING_RECOMMENDATION_KINDS)[number];

export const HEALING_CONFIDENCE_LEVELS = ['low', 'medium', 'high', 'insufficient'] as const;
export type HealingConfidenceLevel = (typeof HEALING_CONFIDENCE_LEVELS)[number];

export const RunFailureHealingCandidateSchema = z.object({
  stepIndex: z.number().int().nonnegative(),
  stepTitle: z.string().nullable(),
  originalLocator: z.string().nullable(),
  healedLocator: z.string().nullable(),
  strategy: z.string().nullable(),
  narrative: z.string().nullable(),
  /** Banded confidence — never a fake percent certainty. */
  confidence: z.enum(HEALING_CONFIDENCE_LEVELS).optional(),
  /** Evidence chips explaining the candidate (deterministic). */
  why: z.array(z.string().max(300)).max(8).optional(),
  /** Pinning into the Flow is a human editor action — draft until saved/published. */
  pinRequiresHumanApproval: z.literal(true),
});
export type RunFailureHealingCandidate = z.infer<typeof RunFailureHealingCandidateSchema>;

/**
 * Feature 2D — composed healing recommendation for a run.
 * Recommendations only; never mutates canvas / publishes / auto-heals.
 */
export const RunHealingIntelligenceSchema = z.object({
  recommendation: z.enum(HEALING_RECOMMENDATION_KINDS),
  confidence: z.enum(HEALING_CONFIDENCE_LEVELS),
  why: z.array(z.string().max(300)).max(12),
  evidence: z.array(z.string().max(300)).max(12),
  candidateCount: z.number().int().nonnegative(),
  selectedStepIndex: z.number().int().nonnegative().nullable(),
  blockedReason: z.string().max(500).nullable().optional(),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
  }),
});
export type RunHealingIntelligence = z.infer<typeof RunHealingIntelligenceSchema>;

export const RunExistingFailureRefSchema = z.object({
  id: z.string().uuid(),
  displayKey: z.string(),
  projectId: z.string().uuid(),
  deepLink: z.string(),
});
export type RunExistingFailureRef = z.infer<typeof RunExistingFailureRefSchema>;

/**
 * QI-1 — Compose-on-read failure explanation sections.
 * Observed facts = deterministic run/Proof only.
 * Inferences = analysis / possibleCause / flake / healing narratives (advisory).
 */
export const FAILURE_EXPLANATION_VERSION_STATUSES = ['CURRENT', 'STALE', 'UNKNOWN'] as const;
export type FailureExplanationVersionStatus = (typeof FAILURE_EXPLANATION_VERSION_STATUSES)[number];

export const FailureExplanationObservedFactSchema = z.object({
  type: z.string().min(1).max(80),
  source: z.enum(['step_results', 'test_runs', 'quality_proof', 'execution', 'classification']),
  value: z.string().max(2_000),
  stepIndex: z.number().int().nonnegative().nullable().optional(),
});
export type FailureExplanationObservedFact = z.infer<typeof FailureExplanationObservedFactSchema>;

export const FailureExplanationInferenceSchema = z.object({
  text: z.string().min(1).max(4_000),
  source: z.enum(['execution_failure_analysis', 'flake_hypothesis', 'healing_narrative', 'healing_intelligence']),
  confidence: z.enum(RUN_FAILURE_CONFIDENCE).optional(),
});
export type FailureExplanationInference = z.infer<typeof FailureExplanationInferenceSchema>;

export const FailureExplanationFailedStepSchema = z.object({
  stepId: z.string().nullable(),
  scenarioStepId: z.string().uuid().nullable().optional(),
  stepIndex: z.number().int().nonnegative(),
  stepTitle: z.string().nullable(),
  stepType: z.string().nullable(),
  assertionKind: z.string().nullable().optional(),
  failureClass: z.enum(EXECUTION_FAILURE_CLASSES).nullable(),
  status: z.string(),
  error: z.string().nullable(),
  errorDetail: z.unknown().nullable().optional(),
  expected: z.string().nullable().optional(),
  actual: z.string().nullable().optional(),
  httpStatus: z.number().int().nullable().optional(),
  retry: z
    .object({
      retryAttempted: z.boolean(),
      retryCount: z.number().int().nonnegative(),
      finalAttempt: z.number().int().nonnegative().nullable().optional(),
      retryReason: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type FailureExplanationFailedStep = z.infer<typeof FailureExplanationFailedStepSchema>;

export const FailureExplanationProofRefSchema = z.object({
  executionId: z.string().uuid().nullable(),
  proofAvailable: z.boolean(),
  evidenceIntegrityStatus: z.enum(['verified', 'unverified']).nullable(),
  /** True when QP-2 verify threw integrity_failed (fail-closed; not overridden). */
  integrityCheckFailed: z.boolean(),
});
export type FailureExplanationProofRef = z.infer<typeof FailureExplanationProofRefSchema>;

export const RunFailureIntelligenceResponseSchema = z.object({
  runId: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  flowId: z.string().uuid().nullable(),
  flowName: z.string().nullable(),
  status: z.string(),
  summary: z.string(),
  failureClass: z.enum(EXECUTION_FAILURE_CLASSES),
  /** Legacy advisory text — also mirrored under inferences (QI-1). Not an observed fact. */
  analysis: z.string(),
  /** Legacy advisory text — deterministic insight only (flake lives in inferences). Not an observed fact. */
  possibleCause: z.string(),
  confidence: z.enum(RUN_FAILURE_CONFIDENCE),
  evidence: RunFailureEvidenceSchema,
  remediations: z.array(RunFailureRemediationSchema),
  healingCandidates: z.array(RunFailureHealingCandidateSchema),
  /** Feature 2D — composed healing recommendation (advisory only). */
  healingIntelligence: RunHealingIntelligenceSchema.optional(),
  flakeHypothesis: z
    .object({
      category: z.string(),
      text: z.string(),
      stepIndex: z.number().int().nullable(),
      provider: z.string().nullable(),
    })
    .nullable(),
  /** Existing Quality HQ failure for this run, if already created. */
  existingFailure: RunExistingFailureRefSchema.nullable(),
  deepLinks: z.object({
    flowEditor: z.string().nullable(),
    qualityFailures: z.string().nullable(),
    healsApi: z.string(),
  }),
  /** Explicit product contract: this payload never mutates Flows. */
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
    /** QI-1 — AI/flake/healing suggestions are advisory only. */
    aiIsAdvisory: z.literal(true),
    /** QI-1 — causality beyond primary=first-failed is unknown unless proven. */
    cascadingCausalityUnknown: z.literal(true),
  }),

  // —— QI-1 compose-on-read failure explanation ——
  executionId: z.string().uuid().nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  scenarioVersion: z.number().int().positive().nullable().optional(),
  currentScenarioVersion: z.number().int().positive().nullable().optional(),
  versionStatus: z.enum(FAILURE_EXPLANATION_VERSION_STATUSES).optional(),
  /** First failed step in execution order — explicit primary; not sole failed step. */
  primaryFailure: FailureExplanationFailedStepSchema.nullable().optional(),
  failedSteps: z.array(FailureExplanationFailedStepSchema).optional(),
  observedFacts: z.array(FailureExplanationObservedFactSchema).optional(),
  proof: FailureExplanationProofRefSchema.optional(),
  inferences: z.array(FailureExplanationInferenceSchema).optional(),
});
export type RunFailureIntelligenceResponse = z.infer<typeof RunFailureIntelligenceResponseSchema>;

/**
 * POST /api/runs/:id/failure-intelligence — create or reuse a draft Quality
 * Failure Intelligence record from this automation run. Never publishes /
 * remediates / mutates Flow canvas.
 */
export const CreateRunFailureIntelligenceResponseSchema = z.object({
  created: z.boolean(),
  reused: z.boolean(),
  runId: z.string().uuid(),
  projectId: z.string().uuid(),
  executionId: z.string().uuid(),
  failure: QualityFailureSchema,
  failures: z.array(QualityFailureSchema),
  deepLink: z.string(),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
    flowMutated: z.literal(false),
    remediationExecuted: z.literal(false),
    rerunTriggered: z.literal(false),
  }),
});
export type CreateRunFailureIntelligenceResponse = z.infer<typeof CreateRunFailureIntelligenceResponseSchema>;

/**
 * POST /api/runs/:id/failure-intelligence/rerun — explicit human approval required.
 * Reuses automation-dispatch; never mutates Flow canvas or publishes.
 */
export const RerunRunFailureIntelligenceRequestSchema = z.object({
  confirm: z.literal(true),
});
export type RerunRunFailureIntelligenceRequest = z.infer<typeof RerunRunFailureIntelligenceRequestSchema>;

export const RerunRunFailureIntelligenceResponseSchema = z.object({
  originalRunId: z.string().uuid(),
  newRunId: z.string().uuid(),
  projectId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string().nullable(),
  originalRanAgainst: z.string().nullable(),
  dispatchStatus: z.enum(['completed', 'queued', 'unavailable']),
  deepLink: z.string(),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
    flowMutated: z.literal(false),
    remediationExecuted: z.literal(false),
    rerunTriggered: z.literal(true),
    humanApproved: z.literal(true),
    executesPublishedCanvas: z.literal(true),
  }),
  versionNote: z.string(),
});
export type RerunRunFailureIntelligenceResponse = z.infer<typeof RerunRunFailureIntelligenceResponseSchema>;
